/**
 * PostcardCollection — Sprint 4A
 * A sober gallery of the city postcards the traveller has earned by lighting a
 * city's whole constellation. The artefact is the reward — no points, no counts.
 *
 * Digital postcards are already unlocked and local (offline). Each free-earned
 * postcard carries a code; "request a physical one" opens losdias.xyz carrying
 * that code (the Los Días postcard pipeline prints + posts it). MySQL is touched
 * only to register the code so losdias.xyz can validate it — best-effort, online.
 */
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getUnlockedPostcards,
  recomputePostcards,
  buildRedeemUrl,
  POSTCARD_PHYSICAL,
} from '../utils/postcardManager';
import PostcardCard from './PostcardCard.jsx';
import theme from '../styles/theme';

export default function PostcardCollection() {
  const { t } = useTranslation('common');
  const [cards, setCards] = useState(null);
  const [active, setActive] = useState(null); // postcard being viewed

  const load = useCallback(async () => {
    // Reconcile cross-trip completion (gems found across different trips, or
    // postcards re-derived after a .trip restore) before showing the gallery.
    await recomputePostcards().catch(() => {});
    const list = await getUnlockedPostcards().catch(() => []);
    setCards(list);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onUnlock = () => load();
    window.addEventListener('wayfare-postcard-unlocked', onUnlock);
    return () => window.removeEventListener('wayfare-postcard-unlocked', onUnlock);
  }, [load]);

  if (cards === null) return null;

  return (
    <div style={S.section}>
      <div style={S.heading}>{t('postcard.collectionTitle')}</div>

      {cards.length === 0 ? (
        <div style={S.empty}>{t('postcard.collectionEmpty')}</div>
      ) : (
        <div style={S.grid}>
          {cards.map((c) => (
            <button key={c.cityId} type="button" style={S.cell} onClick={() => setActive(c)}>
              <PostcardCard city={c.city} country={c.country} img={c.img} size="compact" />
              <div style={S.cellCity}>{c.city}</div>
            </button>
          ))}
        </div>
      )}

      {active && <PostcardDetail postcard={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function PostcardDetail({ postcard, onClose }) {
  const { t, i18n } = useTranslation('common');
  const redeemUrl = buildRedeemUrl(postcard, i18n.language);

  return (
    <div style={S.scrim} role="dialog" aria-modal="true" onClick={onClose}>
      <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
        <PostcardCard city={postcard.city} country={postcard.country} img={postcard.img} />
        <h3 style={S.modalTitle}>{postcard.city}</h3>

        {POSTCARD_PHYSICAL === 'link' && (
          <>
            <p style={S.modalNote}>{t('postcard.physicalNote')}</p>
            {postcard.code && (
              <div style={S.codeRow}>
                <span style={S.codeLabel}>{t('postcard.codeLabel')}</span>
                <span style={S.code}>{postcard.code}</span>
              </div>
            )}
            <a href={redeemUrl} target="_blank" rel="noopener noreferrer"
              style={S.primary} onClick={onClose}>
              {t('postcard.physicalLink')}
            </a>
          </>
        )}

        <button type="button" style={S.ghost} onClick={onClose}>{t('postcard.close')}</button>
      </div>
    </div>
  );
}

const S = {
  section: { marginTop: 16 },
  heading: {
    fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    color: theme.terracottaDark, marginBottom: 12,
  },
  empty: {
    textAlign: 'center', color: theme.brown, fontSize: 14, fontStyle: 'italic',
    padding: '28px 18px', background: theme.card, borderRadius: 14,
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
  },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
  cell: {
    border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', textAlign: 'left',
  },
  cellCity: {
    marginTop: 6, fontSize: 13, fontWeight: 700, color: theme.brownDark,
    fontFamily: "'Noto Serif', serif",
  },
  scrim: {
    position: 'fixed', inset: 0, zIndex: 1450,
    background: 'rgba(26,51,64,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  sheet: {
    width: '100%', maxWidth: 400, background: theme.card,
    borderRadius: 16, padding: 18, boxShadow: '0 12px 40px rgba(0,0,0,0.30)',
  },
  modalTitle: {
    margin: '14px 0 8px', fontSize: 20, fontWeight: 800, color: theme.brownDark,
    fontFamily: "'Noto Serif', serif", textAlign: 'center',
  },
  modalNote: { margin: '0 0 12px', fontSize: 13.5, lineHeight: 1.5, color: theme.brown, textAlign: 'center' },
  codeRow: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
    padding: '10px 0', marginBottom: 12,
    background: theme.cream, borderRadius: 10, border: `1px dashed ${theme.sand}`,
  },
  codeLabel: {
    fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
    color: theme.brown, fontWeight: 600,
  },
  code: {
    fontSize: 18, fontWeight: 800, letterSpacing: '0.12em',
    color: theme.terracottaDark, fontFamily: 'monospace',
  },
  primary: {
    display: 'block', textAlign: 'center', textDecoration: 'none',
    padding: '12px 0', marginBottom: 8,
    background: theme.terracotta, color: '#fff',
    borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer',
  },
  ghost: {
    width: '100%', padding: '10px 0', background: 'transparent',
    border: 'none', color: theme.brown, fontSize: 14, fontWeight: 600, cursor: 'pointer',
  },
};
